import { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "ost_cart_v1";

export function CartProvider({ children }) {
    const [items, setItems] = useState([]);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) setItems(JSON.parse(raw));
        } catch (_) {}
        setHydrated(true);
    }, []);

    useEffect(() => {
        if (hydrated) localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items, hydrated]);

    const addItem = (item) => {
        setItems((prev) => {
            const key = `${item.product_id}::${item.variant_id || ""}`;
            const idx = prev.findIndex((p) => `${p.product_id}::${p.variant_id || ""}` === key);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + (item.quantity || 1) };
                return next;
            }
            return [...prev, { ...item, quantity: item.quantity || 1 }];
        });
    };

    const updateQty = (product_id, variant_id, quantity) => {
        setItems((prev) =>
            prev
                .map((p) =>
                    p.product_id === product_id && (p.variant_id || "") === (variant_id || "")
                        ? { ...p, quantity: Math.max(1, quantity) }
                        : p
                )
                .filter((p) => p.quantity > 0)
        );
    };

    const removeItem = (product_id, variant_id) => {
        setItems((prev) =>
            prev.filter(
                (p) =>
                    !(p.product_id === product_id && (p.variant_id || "") === (variant_id || ""))
            )
        );
    };

    const clear = () => setItems([]);

    const totals = useMemo(() => {
        const subtotal = items.reduce((s, i) => s + Number(i.price || 0) * Number(i.quantity || 0), 0);
        const count = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
        return { subtotal, count };
    }, [items]);

    return (
        <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clear, totals }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
